import { useEffect, useRef, useState } from 'react';
import { Ic } from './Icon';
import { gsap, prefersReducedMotion } from './motion';
import { SectionHead } from './Section';
import type { LandingContent } from './content';

interface Props {
  c: LandingContent;
  onOpenDemo: () => void;
}

const DASH_ROUTE = 'M80 470 C160 440, 200 330, 250 300 S360 260, 450 250 S560 210, 620 120';
const MARKER: Record<string, string> = { v1: 'dm1', v2: 'dm2', v3: 'dm3', v4: 'dm4' };

export function LiveTrackingShowcase({ c, onOpenDemo }: Props) {
  const L = c.live;
  const root = useRef<HTMLDivElement>(null);
  const [activeVeh, setActiveVeh] = useState('v1');
  const [tab, setTab] = useState(0);
  const [routeStep, setRouteStep] = useState(0);

  const next = () => setRouteStep((s) => (s + 1) % L.route.length);

  /* Simulated trip advances every 4 s while the tab is visible. */
  useEffect(() => {
    const id = window.setInterval(() => {
      if (!document.hidden) next();
    }, 4000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [L.route.length]);

  /* Marker motion along the dashboard route. */
  useEffect(() => {
    const el = root.current;
    if (!el || prefersReducedMotion()) return;
    const ctx = gsap.context(() => {
      gsap.to('#dm1', { duration: 14, repeat: -1, yoyo: true, ease: 'none', motionPath: { path: '#route2', align: '#route2', alignOrigin: [0.5, 0.5] } });
      gsap.to('#dm4', { x: 20, y: -10, duration: 5, yoyo: true, repeat: -1, ease: 'sine.inOut' });
    }, el);
    return () => ctx.revert();
  }, []);

  const step = L.route[routeStep]!;
  const v = L.list.find((x) => x.id === activeVeh) ?? L.list[0]!;
  const isV1 = activeVeh === 'v1';
  const offline = v.st === 'offline';
  const spd = isV1 ? `${step.spd} ${L.kmh}` : v.spd;
  const fuel = isV1 ? step.fuel : v.fuel;
  const temp = isV1 ? String(step.temp) : v.temp;
  const initials = v.driver
    .split(' ')
    .slice(0, 2)
    .map((x) => x[0])
    .join('');
  const k = L.k;
  const d = L.dispatch;
  const s = L.sens;

  return (
    <section className="lp-section" id="live" style={{ paddingTop: 0 }}>
      <div className="lp-container">
        <SectionHead eyebrow={L.eyebrow} title={L.title} sub={L.sub} />
        <div className="dash" data-reveal ref={root}>
          <div className="dash-bar">
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <span className="dots" aria-hidden="true">
                <i />
                <i />
                <i />
              </span>
              <span className="proto">
                <span className="live" />
                <b>SIMULATION · TELTONIKA TCP :5027</b>
                <span className="sep">|</span>
                <span>Codec 8 Extended</span>
                <span className="sep">|</span>
                <span>GSM 4G · 28 ms</span>
              </span>
            </div>
            <button type="button" className="step-btn" onClick={next}>
              <Ic name="radar" />
              <span>{L.next}</span>
            </button>
          </div>

          <aside className="dash-side">
            <h4>
              <span>{L.vehicles}</span>
              <span>{L.list.length}</span>
            </h4>
            <div>
              {L.list.map((x) => (
                <button type="button" className={`veh${x.id === activeVeh ? ' active' : ''}`} key={x.id} onClick={() => setActiveVeh(x.id)} aria-pressed={x.id === activeVeh}>
                  <span className={`st ${x.st}`} />
                  <span>
                    <b>{x.name}</b>
                    <span>{x.sub}</span>
                  </span>
                  <span className="spd">{x.id === 'v1' ? `${step.spd} ${L.kmh}` : x.spd}</span>
                </button>
              ))}
            </div>
          </aside>

          <div className="dash-map">
            <svg viewBox="0 0 700 540" preserveAspectRatio="xMidYMid slice" aria-hidden="true" focusable="false">
              <rect width="700" height="540" fill="var(--surface)" />
              <rect width="700" height="540" fill="url(#mgrid)" style={{ color: 'var(--text)' }} />
              <g fill="var(--surface-3)" stroke="var(--border)">
                <rect x="60" y="60" width="140" height="90" rx="8" />
                <rect x="480" y="70" width="160" height="80" rx="8" />
                <rect x="90" y="360" width="120" height="110" rx="8" />
                <rect x="500" y="380" width="140" height="90" rx="8" />
                <rect x="300" y="230" width="110" height="80" rx="8" />
              </g>
              <g fill="none" stroke="var(--border-strong)" strokeWidth="12" strokeLinecap="round" strokeOpacity=".7">
                <path d="M0 200 H700" />
                <path d="M0 340 H700" />
                <path d="M250 0 V540" />
                <path d="M450 0 V540" />
              </g>
              <g fill="none" stroke="var(--surface)" strokeWidth="2" strokeDasharray="14 12">
                <path d="M0 200 H700" />
                <path d="M0 340 H700" />
                <path d="M250 0 V540" />
                <path d="M450 0 V540" />
              </g>
              <path id="route2" d={DASH_ROUTE} fill="none" stroke="url(#routeGrad)" strokeWidth="4" strokeLinecap="round" filter="url(#glowF)" />
              <g id="dm1" style={{ opacity: MARKER[activeVeh] === 'dm1' ? 1 : 0.55 }}>
                <circle r="20" fill="#34d399" fillOpacity=".18" />
                <circle r="9" fill="#34d399" stroke="var(--surface)" strokeWidth="3" />
              </g>
              <g id="dm2" transform="translate(130 105)" style={{ opacity: MARKER[activeVeh] === 'dm2' ? 1 : 0.55 }}>
                <circle r="20" fill="#fbbf24" fillOpacity=".18" />
                <circle r="9" fill="#fbbf24" stroke="var(--surface)" strokeWidth="3" />
              </g>
              <g id="dm3" transform="translate(560 425)" style={{ opacity: MARKER[activeVeh] === 'dm3' ? 1 : 0.55 }}>
                <circle r="9" fill="#94a3b8" stroke="var(--surface)" strokeWidth="3" />
              </g>
              <g id="dm4" transform="translate(355 270)" style={{ opacity: MARKER[activeVeh] === 'dm4' ? 1 : 0.55 }}>
                <circle r="20" fill="#34d399" fillOpacity=".18" />
                <circle r="9" fill="#34d399" stroke="var(--surface)" strokeWidth="3" />
              </g>
            </svg>
            <div className="hud hud-top">
              <div className="loc">
                <span className="ic">
                  <Ic name="route" />
                </span>
                <span>
                  <small>{step.loc}</small>
                  <b>{L.list[0]!.name}</b>
                </span>
              </div>
              <span className="coords">{`LAT ${step.lat}  ·  LNG ${step.lng}`}</span>
            </div>
            <div className="hud hud-bot">
              <span className="m">
                <Ic name="gauge" />
                <span>{k.speed}</span>
                <b>{`${step.spd} ${L.kmh}`}</b>
              </span>
              <span className="m">
                <Ic name="fuel" />
                <span>{k.fuel}</span>
                <b>{`${step.fuel}% · ${step.l} L`}</b>
              </span>
              <span className="m">
                <Ic name="temp" />
                <span>{k.temp}</span>
                <b dir="ltr">{`${step.temp} °C`}</b>
              </span>
            </div>
          </div>

          <aside className="dash-panel">
            <div className="tabs" role="tablist">
              {L.tabs.map((x, i) => (
                <button type="button" role="tab" className="tab" aria-selected={i === tab} key={x} onClick={() => setTab(i)}>
                  {x}
                </button>
              ))}
            </div>
            <div role="tabpanel">
              {tab === 0 ? (
                <div className="kv">
                  <div className="drv">
                    <span className="av">{initials}</span>
                    <span>
                      <b>{v.driver}</b>
                      <small>{L.drv.role}</small>
                    </span>
                    <span className="on">{offline ? '--' : L.drv.on}</span>
                  </div>
                  <Row k={k.speed} v={spd} />
                  <Row k={k.ign} v={v.ign} />
                  <Row k={k.odo} v={v.odo} />
                  <Row k={L.drv.batt} v={offline ? '--' : '27.4 V'} />
                  <div className="row">
                    <span>{k.geo}</span>
                    <b style={{ color: 'var(--emerald)' }}>{offline ? '--' : k.geoV}</b>
                  </div>
                </div>
              ) : tab === 1 ? (
                <div className="kv">
                  <div className="row" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>{k.fuel}</span>
                      <b>
                        {fuel}%{isV1 ? ` · ${step.l} L` : ''}
                      </b>
                    </div>
                    <Bar pct={fuel} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--muted)', marginTop: 8 }}>
                      <span>{s.cap}</span>
                      <span>{s.burn}</span>
                    </div>
                  </div>
                  <div className="row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 4 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>{k.temp}</span>
                      <b dir="ltr">{temp === '--' ? '--' : `${temp} °C`}</b>
                    </div>
                    {isV1 ? <small style={{ fontSize: 11, color: 'var(--muted)' }}>{s.range}</small> : null}
                  </div>
                  <Row k="CAN bus" v={offline ? '--' : 'OK'} />
                  <Row k="GSM" v={offline ? '--' : '-71 dBm'} />
                </div>
              ) : tab === 2 ? (
                <div className="kv">
                  <div className="row">
                    <span>{d.trip}</span>
                    <b style={{ color: 'var(--brand-ink)' }} dir="ltr">{`#TR-2026-89${21 + L.list.findIndex((x) => x.id === activeVeh)}`}</b>
                  </div>
                  <Row k={d.consignee} v={d.consigneeV} />
                  <Row k={d.cargo} v={d.cargoV} />
                  <Row k={d.eta} v={d.etaV} />
                  <div className="row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 4 }}>
                    <b>{d.pod}</b>
                    <small style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.5 }}>{d.podV}</small>
                  </div>
                </div>
              ) : (
                <div className="kv">
                  {L.alerts.map((a) => (
                    <div className={`alert-row sev-${a.sev}`} key={a.t}>
                      <span className="ic">
                        <Ic name={a.sev === 'ok' ? 'check' : 'bell'} />
                      </span>
                      <span>
                        <b style={{ fontWeight: 600 }}>{a.t}</b>
                        <small>{a.s}</small>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button type="button" className="lp-btn lp-btn-primary" onClick={onOpenDemo} style={{ marginTop: 'auto' }}>
              <span>{L.cta}</span>
              <Ic name="arrow" flip />
              <span className="shine" />
            </button>
            <div className="hw">
              <span>
                <Ic name="shield" />
                Teltonika FMC130 4G LTE
              </span>
              <span>
                IMEI <code>352093081452001</code>
              </span>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="row">
      <span>{k}</span>
      <b>{v}</b>
    </div>
  );
}

/** Progress bar that animates from 0 on mount and tweens on value change (CSS transition on width). */
function Bar({ pct }: { pct: number }) {
  const [w, setW] = useState(0);
  useEffect(() => {
    const id = requestAnimationFrame(() => setW(pct));
    return () => cancelAnimationFrame(id);
  }, [pct]);
  return (
    <div className="bar">
      <i style={{ width: `${w}%` }} />
    </div>
  );
}
